import { type FetchStatus, type QueryStatus } from "@tanstack/react-query";

import { isChangeRequestBlockedError } from "@repo/logic";
import { cs } from "@repo/ui";
import ChangeRequestConfirmation, { type IChangeRequestConfirmation } from "./ChangeRequestConfirmation";
import ChangeRequestError, { type IChangeRequestError } from "./ChangeRequestError";
import ChangeRequestLoading, { type IChangeRequestLoading } from "./ChangeRequestLoading";

export const mapQueryStatusToSubmitStatus = (status: QueryStatus | FetchStatus): SubmitStatus => {
    switch (status) {
        case "fetching":
        case "pending":
            return SubmitStatus.Submitting;
        case "error":
            return SubmitStatus.Error;
        case "success":
            return SubmitStatus.Success;
        default:
            return SubmitStatus.Idle;
    }
};

export const mapFetchApiErrorToSubmitStatus = (error: unknown): SubmitStatus => {
    if (isChangeRequestBlockedError(error)) {
        return SubmitStatus.Blocked;
    }

    return SubmitStatus.Error;
};

export enum SubmitStatus {
    Success = "success",
    Submitting = "submitting",
    Blocked = "blocked",
    Error = "error",
    Idle = "idle",
}

// Creates type union from enum.
type StatusType = `${SubmitStatus}`;

type StatusProps<TStepProps> =
    | {
          customElement: JSX.Element;
      }
    | TStepProps;

type ISubmitStatusStepProps = {
    status: StatusType;
    successProps?: StatusProps<IChangeRequestConfirmation>;
    loadingProps?: StatusProps<IChangeRequestLoading>;
    errorProps?: StatusProps<IChangeRequestError>;
};

export const SubmitStatusStep = ({
    status,
    successProps = {},
    loadingProps = {},
    errorProps = {},
}: ISubmitStatusStepProps): JSX.Element => {
    const statusToComponent: Record<SubmitStatus, JSX.Element> = {
        [SubmitStatus.Success]:
            "customElement" in successProps ? (
                successProps.customElement
            ) : (
                <ChangeRequestConfirmation {...successProps} />
            ),
        [SubmitStatus.Submitting]:
            "customElement" in loadingProps ? loadingProps.customElement : <ChangeRequestLoading {...loadingProps} />,
        [SubmitStatus.Blocked]: (
            <ChangeRequestConfirmation description={cs.global.changeIsBlocked} documentsAttentionOverride={null} />
        ),
        [SubmitStatus.Error]:
            "customElement" in errorProps ? errorProps.customElement : <ChangeRequestError {...errorProps} />,
        [SubmitStatus.Idle]: <></>,
    };

    return statusToComponent[status];
};
